import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import React, { useRef, useState } from "react";
import { IFacility } from "redux/reducers/models/facility";
import { IFacilityPortalExtended, useTeeOnPortalContext } from "../../TeeOnPortalContext";
import FacilitiesDropdown from "./FacilitiesDropdown";
import "./teeOnPortalFacilities.scss";
interface IProps {
  facility: IFacility;
  loadTeeTimes: (faciltities: Array<IFacilityPortalExtended>) => Promise<void>;
}

export default function TeeOnPortalFacility(props: IProps) {
  const { t } = useTranslation();
  const { facility, loadTeeTimes } = props;
  const ref = useRef();
  const windowSize = useWindowSize();
  const {
    teeOnPortalState: { facilities, selectedFacilities },
    updateTeeOnPortal,
  } = useTeeOnPortalContext();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  useOutsideAlerter(ref, () => {
    setDropdownOpen(false);
  });

  function handleFacilitySwap(updatedFacilities: Array<IFacilityPortalExtended>) {
    if (updatedFacilities) {
      updateTeeOnPortal({ selectedFacilities: updatedFacilities });
      void loadTeeTimes(updatedFacilities);
    }
  }

  function handleRemoveFacility(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();
    setDropdownOpen(false);
    const updatedFacilities = selectedFacilities?.filter(selectedFacility => selectedFacility?.id !== facility?.id);
    updateTeeOnPortal({ selectedFacilities: updatedFacilities });
    void loadTeeTimes(updatedFacilities);
  }

  return (
    <div
      ref={ref}
      className={classNames("tee-on-portal-facility", {
        "tee-on-portal-facility-badge":
          (selectedFacilities?.length > 2 && windowSize.width > MOBILE_WIDTH) ||
          (selectedFacilities?.length > 1 && windowSize.width <= MOBILE_WIDTH),
      })}
      onClick={() => setDropdownOpen(true)}
    >
      {/* Large facility display */}
      {(selectedFacilities?.length <= 2 && windowSize.width > MOBILE_WIDTH) ||
      (selectedFacilities?.length === 1 && windowSize.width <= MOBILE_WIDTH) ? (
        <div className="tee-on-portal-facility-container">
          <div className="tee-on-portal-facility-logo-container">
            {facility?.logo_source ? (
              <img
                src={facility?.logo_source}
                alt={`${facility?.long_name} Logo`}
                className="tee-on-portal-facility-logo"
              />
            ) : (
              <img
                src="/public/images/default_profile_image.svg"
                alt={t("Facility Logo")}
                className="tee-on-portal-facility-logo"
              />
            )}
          </div>
          <div>
            <p className="tee-on-portal-facility-primary-text">{facility?.long_name}</p>
            <p className="tee-on-portal-facility-secondary-text">
              {facility?.address_line_1}, {facility?.city}, {facility?.province_name}, {facility?.postal}
            </p>
          </div>

          {selectedFacilities?.length > 1 && (
            <div className="tee-on-portal-facility-close-container" onClick={e => handleRemoveFacility(e)}>
              <FontAwesomeIcon icon={faXmark} size="1x" className="tee-on-portal-facility-close" />
            </div>
          )}
        </div>
      ) : (
        // Badge facility display (> 2 select facilities)
        <div className="tee-on-portal-facility-badge-container">
          {facility?.logo_source ? (
            <img
              src={facility?.logo_source}
              alt={`${facility?.long_name} Logo`}
              className="tee-on-portal-facility-badge-logo"
            />
          ) : (
            <img
              src="/public/images/default_profile_image.svg"
              alt={t("Facility Logo")}
              className="tee-on-portal-facility-badge-logo"
            />
          )}

          <p className="tee-on-portal-facility-badge-name">{facility?.long_name}</p>
          <div
            style={{ marginLeft: windowSize.width <= MOBILE_WIDTH ? "auto" : undefined }}
            onClick={e => handleRemoveFacility(e)}
          >
            <FontAwesomeIcon icon={faXmark} size="1x" className="tee-on-portal-facility-badge-close" />
          </div>
        </div>
      )}
      <FacilitiesDropdown
        open={dropdownOpen}
        currentFacility={facility}
        facilities={facilities}
        selectedFacilities={selectedFacilities}
        onFacilitySwap={facilities => handleFacilitySwap(facilities)}
        onClose={() => setDropdownOpen(false)}
        position={selectedFacilities?.length > 2 ? "left" : "center"}
      />
    </div>
  );
}
